import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * Two-level drill-down for the mobile filters drawer (root list + sliding panes).
 * Mirrors header panel subpanel behavior.
 */
class BalanceFiltersDrilldown extends HTMLElement {
  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  /** Pane asked for by the control that opened the drawer (a pinned filter) */
  #requestedPane = '';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    // Capture, so the pane is known before the same click opens the dialog
    window.addEventListener('click', this.#onOpenRequest, true);
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.#dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.#resetToRoot);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('click', this.#onOpenRequest, true);
    this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.#resetToRoot);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const openBtn = target.closest('[data-filter-drilldown-target]');
    if (openBtn instanceof HTMLElement) {
      const id = openBtn.dataset.filterDrilldownTarget;
      if (!id) return;
      event.preventDefault();
      this.#showPane(id);
      return;
    }

    const backBtn = target.closest('[data-filter-drilldown-back]');
    if (backBtn) {
      event.preventDefault();
      this.#showRoot();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.classList.contains('is-drilled-in')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#showRoot();
  };

  #resetToRoot = () => {
    this.#showRoot();
  };

  /** @param {Event} event */
  #onOpenRequest = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-filter-drilldown-open]') : null;
    if (target instanceof HTMLElement) this.#requestedPane = target.dataset.filterDrilldownOpen || '';
  };

  // Opens on the pinned filter that was clicked, else on the collection's
  // default filter (data-default-pane), else on the list of filters.
  #onDialogOpen = () => {
    const paneId = this.#requestedPane || this.#dialogComponent?.dataset.defaultPane || '';
    this.#requestedPane = '';
    this.#showRoot();
    if (paneId) this.#showPane(paneId);
  };

  #showRoot() {
    const root = this.querySelector('[data-filter-drilldown-root]');
    root?.removeAttribute('aria-hidden');
    const panesWrap = this.querySelector('[data-filter-drilldown-panes]');
    panesWrap?.setAttribute('aria-hidden', 'true');
    this.querySelectorAll('.facets-drawer-drilldown__pane').forEach((p) => {
      if (!(p instanceof HTMLElement)) return;
      p.classList.remove('is-active');
      p.setAttribute('aria-hidden', 'true');
    });
    this.classList.remove('is-drilled-in');
    this.querySelectorAll('[data-filter-drilldown-target]').forEach((btn) => {
      if (btn instanceof HTMLElement) btn.setAttribute('aria-expanded', 'false');
    });
  }

  /** @param {string} id */
  #showPane(id) {
    const pane = this.ownerDocument.getElementById(id);
    if (!pane || !this.contains(pane)) return;

    const root = this.querySelector('[data-filter-drilldown-root]');
    root?.setAttribute('aria-hidden', 'true');

    const panesWrap = this.querySelector('[data-filter-drilldown-panes]');
    panesWrap?.setAttribute('aria-hidden', 'false');

    this.querySelectorAll('.facets-drawer-drilldown__pane').forEach((p) => {
      if (!(p instanceof HTMLElement)) return;
      const active = p.id === id;
      p.classList.toggle('is-active', active);
      p.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.classList.add('is-drilled-in');

    this.querySelectorAll('[data-filter-drilldown-target]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const expanded = btn.dataset.filterDrilldownTarget === id;
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }
}

if (!customElements.get('balance-filters-drilldown')) {
  customElements.define('balance-filters-drilldown', BalanceFiltersDrilldown);
}
